import _, { noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Field, useField, useForm } from "react-final-form";
import { ConfirmModal } from "../components/ConfirmModal";
import { DropzoneInput } from "../components/inputs/final_form/DropzoneInput";
import { Fieldset } from "../components/inputs/final_form/Fieldset";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { LoadingButton } from "../components/LoadingButton";
import { api, redirectTo } from "../util/api";
import { snakifyKeys } from "../util/helpers";
import { numericInRange, required } from "../util/validators";
import { trackEvent } from "../util/track";
import { Card, CardTitle } from "reactstrap";
import { StatusTheCaseFields } from "./components/StatusTheCaseFields";

const REQUIRED_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate"],
  denied: [],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: [],
  partially_approved: ["authorizationNumber", "authorizationDate", "approvedCptCodes"],
  submitted: [],
  rejected: [],
};

const DISPLAY_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate", "authorizationEffectiveDate", "authorizationExpirationDate"],
  denied: ["denialReasonCode", "submissionReferenceNumber"],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: ["submissionReferenceNumber", "expectedCloseDate"],
  partially_approved: [
    "authorizationNumber",
    "authorizationDate",
    "authorizationEffectiveDate",
    "authorizationExpirationDate",
    "approvedCptCodes",
    "deniedCptCodes",
    "denialReasonCode",
  ],
  code_specific_result: ["approvedCptCodes", "deniedCptCodes", "documentedCleanClaimCptCodes"],
  submitted: [],
  rejected: [],
};

const ConfirmationButton = ({ children, showModal = false, loading, disabled, onClick, onModalConfirm, ...rest }) => {
  const confirmButton = (
    <LoadingButton type="button" color="primary" loading={loading} disabled={disabled} onClick={onClick} {...rest}>
      {children}
    </LoadingButton>
  );

  return showModal ? (
    <ConfirmModal
      Target={confirmButton}
      onConfirm={onModalConfirm}
      confirmLabel="Confirm"
      title="Proceed to Next Portal"
      confirmMessage="Confirming this status will move the case to the next portal. Are you sure?"
      confirmButtonProps={{ type: "button" }}
    />
  ) : (
    confirmButton
  );
};

export const FieldsetWrapper = ({ displayCondition, labelText, children }) => {
  if (displayCondition) {
    return (
      <Card className="px-3 pt-3 pb-0 mb-3 bg-secondary bg-opacity-10 border-0 shadow-sm">
        <CardTitle className="d-flex flex-row justify-content-between gap-2">
          <p className="fw-semibold w-fit mb-0">{labelText}</p>
        </CardTitle>

        {children}
      </Card>
    );
  }

  return <>{children}</>;
};

export const SubmitRightTestForm = ({
  caseId,
  testId,
  portalId,
  header,
  index,
  request,
  getMessageObj,
  portalMessageOptions,
  denialReasonCodeOptions,
  verifiedCptCodeOptions,
  confirmedTests,
  setConfirmedTests,
  setError,
  nextPortalUrl,
  attachmentCategories = {},
}) => {
  // State Wall
  const form = useForm();
  const portalMessage = useField(`tests[${index}].portalMessage`, { subscription: { value: true } })?.input?.value;

  const approvedCptCodesValue = useField(`tests[${index}].approvedCptCodes`, { subscription: { value: true } })?.input?.value;
  const deniedCptCodesValue = useField(`tests[${index}].deniedCptCodes`, { subscription: { value: true } })?.input?.value;
  const cleanClaimCptCodesValue = useField(`tests[${index}].documentedCleanClaimCptCodes`, { subscription: { value: true } })?.input?.value;

  const [confirmed, _setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const setConfirmed = useCallback(
    (value) => {
      _setConfirmed(value);

      // Also update the parent copy
      const confirmedArray = [...confirmedTests];
      confirmedArray[index] = value;
      setConfirmedTests(confirmedArray);
    },
    [setConfirmedTests, index, confirmedTests]
  );

  // Upon choosing a portal message, we clear out the confirmation.
  // We don't want this hook to fire any other time; the linter is wrong here.
  useEffect(() => {
    if (confirmed) {
      setConfirmed(false);
    }
  }, [portalMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const messageObj = !_.isEmpty(portalMessage) && getMessageObj(portalMessage);

  const { status } = messageObj;

  // other handlers

  const isCodeSpecificResult = useMemo(() => status === "code_specific_result", [status]);

  const handleConfirm = () => {
    setLoading(true);

    if (messageObj?.message !== undefined) {
      form.change(`tests[${index}].cannedComment`, messageObj.canned_comment);
      if (messageObj.submit_right_direction == "status_the_case") {
        form.change(`tests[${index}].status`, messageObj.status);
        form.change(`tests[${index}].substatus`, messageObj.substatus);

        if (messageObj?.denial_reason_code) {
          form.change(`tests[${index}].denialReasonCode`, messageObj?.denial_reason_code);
        }
      } else if (messageObj.submit_right_direction == "task_out_for_n_number_of_days_to_try_again") {
        form.change(`tests[${index}].taskName`, messageObj.task_name);
        form.change(`tests[${index}].taskDuration`, messageObj.task_duration);

        trackEvent("task_out_case_sr2", caseId, {
          step: "task_out_case_sr2",
        });
      }
    }

    setConfirmed(true);
    setLoading(false);
  };

  const confirmProceedNextPortal = async (cannedComment) => {
    setLoading(true);
    setError(null);

    const params = {
      id: caseId,
      portalId: portalId,
      cannedComment: cannedComment,
    };

    try {
      await api.post(nextPortalUrl, snakifyKeys(params));

      trackEvent("proceed_to_next_portal_confirm_button_sr2", caseId, {
        step: "proceed_to_next_portal_confirm_button_sr2",
      });

      redirectTo("/workflow/" + caseId);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  return (
    <Fieldset legend={header}>
      <Field component={TextInput} type="hidden" name={`tests[${index}].testId`} initialValue={testId} />
      <Field component={SelectInput} label="Portal Message" name={`tests[${index}].portalMessage`} options={portalMessageOptions} />

      {confirmed && messageObj?.submit_right_direction === "task_out_for_n_number_of_days_to_try_again" && (
        <>
          <Field component={TextInput} label="Task Name" name={`tests[${index}].taskName`} disabled={true} />
          <Field
            component={TextInput}
            type="number"
            name={`tests[${index}].taskDuration`}
            label="Task Duration(Days)"
            validate={numericInRange(0, 90)}
            min="0"
            max="90"
            disabled={true}
          />
        </>
      )}

      {confirmed && messageObj?.submit_right_direction === "status_the_case" && (
        <>
          <StatusTheCaseFields
            verifiedCptCodeOptions={verifiedCptCodeOptions}
            isCodeSpecificResult={isCodeSpecificResult}
            status={status}
            displayStatusMap={DISPLAY_STATUS_FIELDS}
            requiredStatusMap={REQUIRED_STATUS_FIELDS}
            formLink={`tests[${index}]`}
            request={request}
            denialReasonCodeOptions={denialReasonCodeOptions}
            messageObj={messageObj}
            approvedCptCodes={approvedCptCodesValue}
            deniedCptCodes={deniedCptCodesValue}
            cleanClaimCptCodes={cleanClaimCptCodesValue}
          />
        </>
      )}

      {confirmed && messageObj && (
        <>
          <Field component={TextInput} label="Canned Comment" name={`tests[${index}].cannedComment`} />
          <Field component={DropzoneInput} label="Attach File" name={`tests[${index}].attachment`} />

          <Field
            component={SelectInput}
            label="Attachment Category"
            name={`tests[${index}].attachmentCategory`}
            options={_.map(attachmentCategories, (c, key) => ({ label: c, value: key }))}
          />
        </>
      )}

      {!confirmed && (
        <ConfirmationButton
          showModal={messageObj?.submit_right_direction === "proceed_to_the_next_portal"}
          onClick={handleConfirm}
          onModalConfirm={() => confirmProceedNextPortal(messageObj.canned_comment)}
          loading={loading}
          disabled={loading || _.isEmpty(portalMessage)}
        >
          Confirm Response
        </ConfirmationButton>
      )}
    </Fieldset>
  );
};
